import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { authAPI } from '../services/api';

// Phone/SMS is the PRIMARY reset method — many accounts (e.g. anything
// created via the /dwifi captive portal) have an unverified or synthetic
// email that a mailed reset link could never reach. Email stays available
// as a secondary option for accounts that do have a real, checked inbox.
function PhoneReset() {
  const [step, setStep] = useState('phone'); // 'phone' -> 'reset' -> 'done'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    if (!phone.trim()) { toast.error('Enter your phone number'); return; }
    setLoading(true);
    try {
      await authAPI.forgotPasswordPhone(phone.trim());
    } catch (err) {
      // Rate limits etc. still show an error; a "no such account" case
      // deliberately looks identical to success server-side, so it never
      // reaches this catch block just because the account doesn't exist.
      toast.error(err.message || 'Request failed. Try again.');
      setLoading(false);
      return;
    }
    setLoading(false);
    setStep('reset'); // always advance — never reveals whether the account exists
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) { toast.error('Enter the 6-digit code from the SMS'); return; }
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await authAPI.resetPasswordPhone({ phone: phone.trim(), otp, newPassword });
      setStep('done');
    } catch (err) {
      toast.error(err.message || 'Could not reset password. Check the code and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'done') {
    return (
      <div className="text-center py-4">
        <div className="w-16 h-16 rounded-full bg-green/10 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-text-primary font-bold text-xl mb-2">Password Reset</h2>
        <p className="text-text-secondary text-sm mb-6">Your password has been changed. You can now log in with your new password.</p>
        <Link to="/login" className="inline-block w-full py-3 rounded-xl font-bold text-white text-sm text-center"
          style={{ background: 'linear-gradient(135deg, #00A8DB, #8B5CF6)' }}>
          Go to Login
        </Link>
      </div>
    );
  }

  if (step === 'reset') {
    return (
      <>
        <h2 className="text-text-primary font-bold text-2xl mb-1">Enter Code &amp; New Password</h2>
        <p className="text-text-secondary text-sm mb-6">
          If <strong className="text-text-primary">{phone}</strong> has an account, a 6-digit code was sent by SMS. It's valid for 10 minutes.
        </p>
        <form onSubmit={submitReset} className="space-y-4">
          <div>
            <label className="block text-text-secondary text-sm font-medium mb-2">SMS Code</label>
            <input
              type="text" inputMode="numeric" maxLength={6} value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              className="w-full bg-bg border border-border rounded-xl px-4 py-3 text-text-primary text-sm outline-none focus:border-cyan transition-colors placeholder:text-text-muted tracking-widest"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-sm font-medium mb-2">New Password</label>
            <input
              type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 6 characters"
              className="w-full bg-bg border border-border rounded-xl px-4 py-3 text-text-primary text-sm outline-none focus:border-cyan transition-colors placeholder:text-text-muted"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-sm font-medium mb-2">Confirm New Password</label>
            <input
              type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              className="w-full bg-bg border border-border rounded-xl px-4 py-3 text-text-primary text-sm outline-none focus:border-cyan transition-colors placeholder:text-text-muted"
            />
          </div>
          <button type="submit" disabled={loading}
            className="w-full py-3 rounded-xl font-bold text-white text-sm transition-opacity disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #00A8DB, #8B5CF6)' }}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
        <div className="mt-4 text-center">
          <button onClick={() => setStep('phone')} className="text-text-muted text-sm hover:text-cyan transition-colors">
            ← Use a different phone number
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <h2 className="text-text-primary font-bold text-2xl mb-1">Forgot Password?</h2>
      <p className="text-text-secondary text-sm mb-6">Enter your phone number and we'll text you a verification code.</p>
      <form onSubmit={requestOtp} className="space-y-4">
        <div>
          <label className="block text-text-secondary text-sm font-medium mb-2">Phone Number</label>
          <input
            type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
            placeholder="07XXXXXXXX"
            className="w-full bg-bg border border-border rounded-xl px-4 py-3 text-text-primary text-sm outline-none focus:border-cyan transition-colors placeholder:text-text-muted"
          />
        </div>
        <button type="submit" disabled={loading}
          className="w-full py-3 rounded-xl font-bold text-white text-sm transition-opacity disabled:opacity-60"
          style={{ background: 'linear-gradient(135deg, #00A8DB, #8B5CF6)' }}>
          {loading ? 'Sending...' : 'Send Code'}
        </button>
      </form>
    </>
  );
}

function EmailReset() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Enter your email address'); return; }
    setLoading(true);
    try {
      await axios.post('/api/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Request failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="text-center py-4">
        <div className="w-16 h-16 rounded-full bg-green/10 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-text-primary font-bold text-xl mb-2">Check Your Email</h2>
        <p className="text-text-secondary text-sm mb-6">
          If <strong className="text-text-primary">{email}</strong> is registered, we've sent a password reset link. Check your inbox and spam folder.
        </p>
        <p className="text-text-muted text-xs">The link expires in 1 hour.</p>
      </div>
    );
  }

  return (
    <>
      <h2 className="text-text-primary font-bold text-2xl mb-1">Forgot Password?</h2>
      <p className="text-text-secondary text-sm mb-6">Enter your email and we'll send a reset link.</p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-text-secondary text-sm font-medium mb-2">Email Address</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com" required
            className="w-full bg-bg border border-border rounded-xl px-4 py-3 text-text-primary text-sm outline-none focus:border-cyan transition-colors placeholder:text-text-muted"
          />
        </div>
        <button type="submit" disabled={loading}
          className="w-full py-3 rounded-xl font-bold text-white text-sm transition-opacity disabled:opacity-60"
          style={{ background: 'linear-gradient(135deg, #00A8DB, #8B5CF6)' }}>
          {loading ? 'Sending...' : 'Send Reset Link'}
        </button>
      </form>
    </>
  );
}

export default function ForgotPassword() {
  const [mode, setMode] = useState('phone'); // 'phone' (primary) | 'email' (secondary)

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center font-black text-xl text-white"
              style={{ background: 'linear-gradient(135deg, #00A8DB, #8B5CF6)' }}>D</div>
            <div className="text-left">
              <p className="font-black text-text-primary text-xl tracking-tight">DWI FINTECH</p>
              <p className="text-text-muted text-xs">Password Recovery</p>
            </div>
          </div>
        </div>

        <div className="bg-bg-card border border-border rounded-2xl p-8 shadow-2xl">
          <div className="flex rounded-xl bg-bg p-1 mb-6 text-sm font-semibold">
            <button
              onClick={() => setMode('phone')}
              className="flex-1 py-2 rounded-lg transition-colors"
              style={mode === 'phone' ? { background: 'linear-gradient(135deg, #00A8DB, #8B5CF6)', color: '#fff' } : { color: 'var(--text-muted, #8b98ab)' }}
            >
              Reset by Phone
            </button>
            <button
              onClick={() => setMode('email')}
              className="flex-1 py-2 rounded-lg transition-colors"
              style={mode === 'email' ? { background: 'linear-gradient(135deg, #00A8DB, #8B5CF6)', color: '#fff' } : { color: 'var(--text-muted, #8b98ab)' }}
            >
              Reset by Email
            </button>
          </div>

          {mode === 'phone' ? <PhoneReset /> : <EmailReset />}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-text-muted text-sm hover:text-cyan transition-colors">
              ← Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
